import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { webOrigin } from '../utils/brand';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import useIsMobile from '../hooks/useIsMobile';
import useImageRatios from '../hooks/useImageRatios';
import useMediaQuery from '../utils/useMediaQuery';
import { SurfacePage, SoftSurface, StickyCtaBar } from '../components/surface';
import ClubCover from '../components/club/ClubCover';
import { coverFromClub, coverSlice } from '../components/club/clubCoverModel';
import { ClubCtas, ClubIdentityCard, MobileStats } from '../components/club/ClubHeader';
import { AboutPanel, RitualPanel } from '../components/club/ClubAboutTab';
import { ClubNumbers } from '../components/club/ClubRail';
import { ClubPanel, PanelHead, PillButton, EmptyBlock, ClubBackPill, Toast, useToast, MUTED } from '../components/club/clubUi';
import { resolveLayout } from '../components/event/coverLayout';
import { clubService } from '../services';
import { monthYearLabel, plural } from '../utils/surfaceFormat';

/**
 * The read-only public face of a club, at /clubs/:slugOrId for logged-out visitors (README §1
 * "Logged out"): the same cover and CTAs as the club page, no tabs; WHAT THIS CLUB IS · RITUAL ·
 * an EVENTS card that counts without listing; the rail LOG IN TO JOIN + numbers + share. Members,
 * forum, posts, polls and the invite code are absent because the backend never sends them
 * (`PublicClubDto`), so this page cannot leak them. Signed-in visitors get the full ClubPage.
 */
export default function PublicClubPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const wideQuery = useMediaQuery('(min-width: 1040px)');
  const wide = !mobile && (wideQuery || (typeof window !== 'undefined' && typeof window.matchMedia !== 'function'));
  const { toast, say } = useToast();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await clubService.getPublicClub(id);
      if (cancelled) return;
      setClub(res.success ? res.data : null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  const cover = useMemo(() => coverFromClub(club), [club]);
  const ratios = useImageRatios(cover.photos.map((p) => p.src));
  const shaped = useMemo(() => ({ ...cover, photos: cover.photos.map((p) => ({ ...p, r: ratios[p.src] || p.r, measured: !!ratios[p.src] })) }), [cover, ratios]);
  const photos = useMemo(() => coverSlice(shaped), [shaped]);

  if (loading) return <LoadingSpinner fullScreen />;

  if (!club) {
    // A private club and a club that doesn't exist look identical here, on purpose —
    // confirming a private room exists is itself a leak.
    return (
      <div className="min-h-screen bg-outside-bg">
        <EmptyState title="Club not found" description="This club is private, or it doesn't exist." actionLabel="SEE WHAT'S ON" onAction={() => navigate('/featured')} className="text-white" />
      </div>
    );
  }

  const joinHref = `/login?redirect=${encodeURIComponent(`/clubs/${club.slug || id}`)}`;
  const goLogin = () => navigate(joinHref);
  const share = async () => {
    const url = `${webOrigin()}/clubs/${club.slug || club.id}`;
    try {
      if (navigator.share) { await navigator.share({ title: club.name, url }); return; }
      await navigator.clipboard.writeText(url);
      say('LINK COPIED');
    } catch { /* dismissed */ }
  };
  const onAction = (a) => (a === 'login' ? goLogin() : a === 'share' ? share() : undefined);
  const state = { pill: { label: 'PUBLIC', tone: 'lime' }, trust: { label: 'OPEN TO ANYONE', color: '#000' }, ctas: [{ label: 'LOG IN TO JOIN', fill: true, action: 'login' }, { label: 'SHARE', action: 'share' }] };
  const upcoming = club.upcomingEventCount ?? 0;
  const since = monthYearLabel(club.createdAt);
  const clubForNumbers = { ...club, eventCount: upcoming };

  const main = (
    <div className="flex flex-col font-mono" style={{ gap: mobile ? 10 : 16 }} data-testid="public-about">
      <AboutPanel club={club} phone={mobile} />
      <RitualPanel club={club} phone={mobile} />
      <ClubPanel phone={mobile}>
        <PanelHead title="EVENTS" />
        <EmptyBlock title={`${plural(upcoming, 'UPCOMING EVENT')}`} body="The club’s public events are listed once you log in." action="LOG IN TO SEE THEM" onAction={goLogin} />
      </ClubPanel>
    </div>
  );

  const rail = (
    <div className="flex flex-col font-mono" style={{ gap: 12 }} data-testid="public-rail">
      <ClubPanel phone={mobile}>
        <PanelHead title="LOG IN TO JOIN" right="FREE" />
        <p className="m-0" style={{ fontSize: 13, lineHeight: 1.6 }}>Log in or make an account to join. Posts, the forum and the members list open once you&rsquo;re in.</p>
        <PillButton size={52} fontSize={12} onClick={goLogin} data-testid="public-login">LOG IN TO JOIN</PillButton>
        <PillButton variant="tertiary" size={44} fontSize={11} weight={700} onClick={() => navigate('/featured')}>SEE WHAT&rsquo;S ON</PillButton>
        <span style={{ fontSize: 11, lineHeight: 1.55, color: MUTED }}>Joining is free. Nothing is charged, ever.</span>
      </ClubPanel>
      <ClubNumbers club={clubForNumbers} postsPerWeek={0} phone={mobile} />
      <div className="flex" style={{ gap: 6 }}>
        <PillButton variant="ghost" size={44} fontSize={10} style={{ flex: 1 }} onClick={share}>SHARE</PillButton>
      </div>
    </div>
  );

  const coverNode = <ClubCover club={club} state={state} members={[]} memberCount={club.memberCount ?? 0} since={since} photos={photos} layout={resolveLayout(shaped.layout, photos)} split={shaped.split} video={club.coverVideo} phone={mobile} />;
  const ctas = <ClubCtas ctas={state.ctas} onAction={onAction} phone={mobile} />;

  return (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: 1280, padding: mobile ? '12px 12px 24px' : '24px 32px 72px', gap: mobile ? 10 : 16, boxSizing: 'border-box' }} data-testid="public-club-page">
          <div className="flex items-center"><ClubBackPill to="/clubs" size={mobile ? 36 : 40}>CLUBS</ClubBackPill></div>
          {coverNode}
          {mobile && <ClubIdentityCard club={club} state={state} />}
          {mobile && <MobileStats stats={[{ n: club.memberCount ?? 0, k: 'MEMBERS' }, { n: upcoming, k: 'COMING UP' }, { n: since.replace(/ (\d\d)(\d\d)$/, " '$2"), k: 'SINCE' }]} />}
          {ctas}
          {wide ? (
            <div className="grid items-start" style={{ gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 16 }}>
              {main}
              <div style={{ position: 'sticky', top: 88 }}>{rail}</div>
            </div>
          ) : (
            <div className="flex flex-col" style={{ gap: mobile ? 10 : 16 }}>{main}{rail}</div>
          )}
          {mobile && <StickyCtaBar price="FREE" note="OPEN TO ANYONE" cta="LOG IN" onCta={goLogin} testId="public-cta-bar" />}
        </main>
        <Toast text={toast} phone={mobile} />
      </SurfacePage>
    </SoftSurface>
  );
}
